import auth from '@react-native-firebase/auth';
import { UserServiceClient } from 'fitbeat.api-clients';
import React, { PureComponent } from 'react';
import { translate } from 'react-i18next';
import {
  Keyboard,
  ScrollView,
  StyleProp,
  StyleSheet,
  Text,
  TextStyle,
  View,
} from 'react-native';
import { usesMetricSystem } from 'react-native-localize';
import { connect } from 'react-redux';
import { Field, reduxForm, SubmissionError } from 'redux-form';
import { Button, ButtonSize } from '../components';
import { getApiConfig } from '../config/index';
import { GoogleAnalytics } from '../services/google-analytics';
import Theme, { ThemeType } from '../theme';
import { FormError, TextInput } from '../ui';
import { isEmailValid, isNameValid, isRequired, minLength6 } from '../utils';
import { AuthTokenBuilder } from '../utils/authTokenBuilder';
import firebaseErrorHandler from '../utils/FirebaseErrors';
import { focusOnInput } from '../utils/FocusOnInput';
import { defaultScale, scaleText } from '../utils/scaling';
import { isMobileWeb } from '../utils/ScreenSizes';
import { AlreadyHaveAnAccount, TermsAndConditions } from './components';
import { addLegalAgreements } from './components/utils';
import { styles as SharedStyles } from './SignupFormsStyles';

interface IThemeStyle {
  textStyle: StyleProp<TextStyle>;
}

const themeStyleMapper: { [key in ThemeType]: IThemeStyle } = {
  [ThemeType.light]: {
    textStyle: {
      color: Theme.BLACK,
    },
  },
  [ThemeType.dark]: {
    textStyle: {
      color: Theme.WHITE,
    },
  },
};

interface IProps {
  error?: Error;
  navigation: any;
  workoutInviteId?: string;
  planId?: string;
  firstname: string;
  lastname: string;
  email: string;
  stripeUserId: string;
  pendingPlanId: string;
  pendingCoupon?: string;
  initialize(values: any): void;
  t(key: string, options?: any): string;
  handleSubmit(cd: any): void;
}

interface IState {
  termsAccepted: boolean;
  isLoading: boolean;
}

class CreateEliteAccountScreen extends PureComponent<IProps, IState> {
  public state = {
    termsAccepted: false,
    isLoading: false,
  };

  private googleAnalytics = new GoogleAnalytics();
  private emailInput: any;
  private passwordInput: any;
  private lastnameInput: any;
  private unAuthenticatedUserServiceClient = new UserServiceClient(getApiConfig(), fetch, console.log, undefined);
  private authenticatedUserServiceClient =
    new UserServiceClient(getApiConfig(), fetch, console.log, new AuthTokenBuilder());

  constructor(props: any) {
    super(props);
  }

  public componentDidMount() {
    const { initialize, email, firstname, lastname } = this.props;
    initialize({ email, firstname, lastname });
  }

  public submit = async (values: any) => {
    const { t } = this.props;
    const { termsAccepted } = this.state;

    if (!termsAccepted) {
      throw new SubmissionError({ _error: new Error(t('pleaseAcceptTerms')) });
    }

    this.setState({ isLoading: true });
    const preSignupAccountWithEmail = await this.unAuthenticatedUserServiceClient.getUserForPreAccountByEmail(
      values.email);

    const isExistingUserWithNoContinueToken = preSignupAccountWithEmail
      && !preSignupAccountWithEmail.continueSignupToken;
    if (isExistingUserWithNoContinueToken) {
      this.setState({ isLoading: false });
      const existingAccountError = new Error(t('existingAccountError'));
      throw new SubmissionError({ _error: existingAccountError });
    }

    await this.nonPreAccountFlow(values, preSignupAccountWithEmail);
  }

  public nonPreAccountFlow = async (values, preSignupAccountWithEmail) => {
    try {
      this.setState({ isLoading: true });

      const user = await this.createFirebaseUser(
        values.email,
        values.password,
      );

      this.googleAnalytics.setUserId(user.uid);
      const { firstname, lastname, stripeUserId, pendingPlanId, pendingCoupon, planId } = this.props;
      const userToCreate = {
        uid: user.uid,
        email: user.email,
        firstname,
        lastname,
        currentPlanId: planId,
        linkUserToStripeAccount: stripeUserId,
        pendingPlanId,
        pendingCoupon,
        useImperialUnit: !usesMetricSystem(),
      };
      addLegalAgreements(userToCreate);

      await this.authenticatedUserServiceClient.createUser(userToCreate);
      this.googleAnalytics.logEventWithParamaters('sign_up', { method: 'email' });
      this.props.navigation.navigate('HomeNavigator');
    } catch (error) {
      this.googleAnalytics.logEventWithParamaters('sign_up_failed', { error });
      throw new SubmissionError({ _error: error });
    } finally {
      this.setState({ isLoading: false });
    }
  }

  public createFirebaseUser = async (email, password) => {
    try {
      const result = await auth().createUserWithEmailAndPassword(email, password);
      return result.user;
    } catch (error) {
      throw firebaseErrorHandler(error);
    }
  }

  public acceptTermsAndConditions = () => {
    this.setState({ termsAccepted: !this.state.termsAccepted });
    Keyboard.dismiss();
  }

  public login = () => {
    this.props.navigation.navigate('Login');
  }

  public render() {
    const { t, error, handleSubmit, firstname } = this.props;
    const { isLoading, termsAccepted } = this.state;
    const themeStyle = themeStyleMapper[ThemeType.light];

    return (
      <View style={[SharedStyles.container, styles.container]}>
        <ScrollView>
          <View style={styles.headingContainer}>
            {firstname &&
              <Text style={styles.heading}>{t('createEliteAccountHeadingHey')} <Text
                style={styles.headingBold}>{firstname}</Text>{t('createEliteAccountHeadingSetPasswordOrLogin')}</Text>
            }
            {!firstname &&
              <Text style={styles.heading}>{t('createEliteAccountHeadingSetPasswordOrLoginNoFirstName')}</Text>
            }
          </View>
          <View style={styles.formContainer}>
            <Field
              name={'firstname'}
              returnKeyType={'next'}
              autoCapitalize={'words'}
              placeholder={t('formFields:firstname')}
              height={45}
              component={TextInput}
              validate={isNameValid}
              keyboardType={'default'}
              onSubmitEditing={() => focusOnInput(this.lastnameInput)}
            />
            <Field
              forwardRef
              name={'lastname'}
              returnKeyType={'next'}
              autoCapitalize={'words'}
              placeholder={t('formFields:lastname')}
              component={TextInput}
              validate={isNameValid}
              height={45}
              keyboardType={'default'}
              ref={(ref) => this.lastnameInput = ref}
              onSubmitEditing={() => focusOnInput(this.emailInput)}
            />
            <Field
              forwardRef
              name={'email'}
              returnKeyType={'next'}
              autoCapitalize={'none'}
              placeholder={t('formFields:email')}
              component={TextInput}
              validate={isEmailValid}
              height={45}
              keyboardType={'email-address'}
              ref={(ref) => this.emailInput = ref}
              onSubmitEditing={() => focusOnInput(this.passwordInput)}
            />
            <Field
              forwardRef
              name={'password'}
              secureTextEntry
              returnKeyType={'next'}
              autoCapitalize={'none'}
              component={TextInput}
              height={45}
              placeholder={t('formFields:password')}
              validate={[isRequired, minLength6]}
              ref={(ref) => this.passwordInput = ref}
            />
          </View>

          <TermsAndConditions
            canAccept={true}
            isMinor={false}
            textStyle={styles.tandcText}
            preAgreementText={t('byCreatingYouAgreeToTandC')}
            acceptTermsAndConditions={this.acceptTermsAndConditions}
            termsAccepted={termsAccepted}
            checkBoxSize={defaultScale(20)}
          />

          <FormError {...themeStyle.textStyle} error={error} />

          <AlreadyHaveAnAccount
            t={t}
            login={this.login}
            style={{ marginTop: defaultScale(15) }}
          />

        </ScrollView>
        <Button
          title={t('setupMembership')}
          size={ButtonSize.MEDIUM}
          isLoading={isLoading}
          onPress={handleSubmit(this.submit)}
          overrideFontStyles={[Theme.textStyle.bodyCopyBold, { color: Theme.WHITE }]}
        />
      </View>
    );
  }
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: Theme.WHITE,
    padding: 15,
    paddingTop: isMobileWeb() ? defaultScale(48) : 0,
    height: isMobileWeb() ? '100%' : 'auto',
  },
  heading: {
    ...Theme.textStyle.display1,
  },
  headingBold: {
    ...Theme.textStyle.display1Bold,
  },
  headingContainer: {
    marginBottom: defaultScale(20),
  },
  formContainer: {
    paddingBottom: 15,
  },
  textInput: {
    color: Theme.WHITE,
  },
  tandcText: {
    ...Theme.textStyle.bodyCopyReg,
    color: Theme.GREY_MEDIUM,
  },
  errorContainer: {
    flexDirection: 'row',
  },
  message: {
    flex: 1,
    fontSize: 14,
    flexWrap: 'wrap',
    fontFamily: Theme.CustomBold,
    color: Theme.WHITE,
    paddingVertical: 3,
  },
});

const mapStateToProps = (state) => {
  const {
    workoutInviteId,
    planId,
    first_name,
    last_name,
    email,
    stripe_customer_id,
    pendingPlanId,
    pendingCoupon,
  } = state.deepLinkParamaters;
  return {
    workoutInviteId,
    planId,
    pendingPlanId,
    pendingCoupon,
    firstname: first_name,
    lastname: last_name,
    stripeUserId: stripe_customer_id,
    email,
  };
};

export const CreateEliteAccount = reduxForm({ form: 'CreateHomeAccountForm' })(
  translate(['auth', 'formFields'], { wait: true })(
    connect(mapStateToProps)(CreateEliteAccountScreen)),
);
